import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Shield, X } from "lucide-react";
import { Button } from "@/components/ui/button";

const COOKIE_KEY = "procureaide_cookie_consent";

export const CookieConsent = () => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const consent = localStorage.getItem(COOKIE_KEY);
    if (!consent) {
      const timer = setTimeout(() => setVisible(true), 1500);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAccept = () => {
    localStorage.setItem(COOKIE_KEY, "accepted");
    setVisible(false);
  };

  const handleDecline = () => {
    localStorage.setItem(COOKIE_KEY, "declined");
    setVisible(false);
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ type: "spring", stiffness: 300, damping: 30 }}
          className="fixed bottom-0 left-0 right-0 z-[9998] p-4"
          data-testid="cookie-consent-banner"
        >
          <div className="max-w-4xl mx-auto bg-[#0B1426] border border-[#00B4D8]/20 rounded-xl p-5 sm:p-6 shadow-[0_0_40px_rgba(0,180,216,0.08)]">
            <div className="flex items-start gap-4">
              <div className="hidden sm:flex w-10 h-10 rounded-full bg-[#00B4D8]/10 items-center justify-center flex-shrink-0 mt-0.5">
                <Shield className="w-5 h-5 text-[#00B4D8]" />
              </div>
              <div className="flex-1">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h3 className="text-[#F8FAFC] font-semibold text-sm mb-1.5">We value your privacy</h3>
                    <p className="text-[#94A3B8] text-xs leading-relaxed">
                      We use cookies to enhance your browsing experience, serve relevant content, and analyze our traffic. 
                      By clicking "Accept All", you consent to our use of cookies. Read our{" "}
                      <Link to="/privacy" className="text-[#00B4D8] hover:underline">Privacy Policy</Link> for more information.
                    </p>
                  </div>
                  <button
                    onClick={handleDecline}
                    className="text-[#64748B] hover:text-[#94A3B8] transition-colors flex-shrink-0"
                    data-testid="cookie-close-btn"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
                <div className="flex flex-wrap gap-3 mt-4">
                  <Button
                    onClick={handleAccept}
                    size="sm"
                    className="bg-[#00B4D8] text-[#020617] font-semibold hover:bg-[#0096B4] text-xs px-5"
                    data-testid="cookie-accept-btn"
                  >
                    Accept All
                  </Button>
                  <Button
                    onClick={handleDecline}
                    size="sm"
                    variant="outline"
                    className="border-[#00B4D8]/30 text-[#94A3B8] hover:text-[#F8FAFC] hover:bg-[#111F36] text-xs px-5"
                    data-testid="cookie-decline-btn"
                  >
                    Essential Only
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default CookieConsent;
